import React, { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { apiClient } from '../api/client';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      await apiClient.post('/api/contact', formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Reset to idle after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to send message. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-stone-900 dark:text-white mb-2">Contact Support</h1>
          <p className="text-stone-600 dark:text-foreground-muted">
            Have questions or feedback? We'd love to hear from you.
          </p>
        </div>

        <Card className="bg-white dark:bg-black/30 border-stone-200 dark:border-coral-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-stone-900 dark:text-white">
              <Mail className="w-5 h-5 text-coral-400" />
              Send us a message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 dark:text-foreground-muted mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-coral-400/50 focus:outline-none focus:ring-2 focus:ring-coral-500"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700 dark:text-foreground-muted mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-coral-400/50 focus:outline-none focus:ring-2 focus:ring-coral-500"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-stone-700 dark:text-foreground-muted mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-coral-400/50 focus:outline-none focus:ring-2 focus:ring-coral-500"
                  placeholder="Brief description of your inquiry"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-700 dark:text-foreground-muted mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-coral-400/50 focus:outline-none focus:ring-2 focus:ring-coral-500 resize-none"
                  placeholder="Tell us more about your question or feedback..."
                />
              </div>

              {status === 'success' && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">
                    Thank you for contacting us! We'll get back to you shortly via email.
                  </p>
                </div>
              )}

              {status === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 text-white rounded-lg font-medium transition-colors"
              >
                {status === 'sending' ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 p-4 bg-coral-500/5 border border-coral-500/20 rounded-lg">
          <h3 className="text-sm font-medium text-stone-800 dark:text-foreground-muted mb-2">Other ways to reach us:</h3>
          <ul className="text-sm text-stone-600 dark:text-foreground-muted space-y-1">
            <li>
              Email: <a href="mailto:info@vpoint.app" className="underline underline-offset-2">info@vpoint.app</a>
            </li>
            <li>Response time: Within 24 hours</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
